import { AfterViewInit, Component, Inject, OnInit, ViewChild, ViewChildren } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { TranslateService } from '@ngx-translate/core'
import { ConfirmService, HttpLoaderService, ProductTypeEnum } from '@tymes4-shared';
import { DiscountRejectionReason } from '../../../shared/enums/discount-rejection-reasons';
import { DialogHelper, DialogWidth } from '@tymes4-shared';
import { forkJoin } from 'rxjs';
import { AddDiscountCodeFormComponent } from '../add-discount-code-form/add-discount-code-form.component';
import { EditOrderLineFormComponent } from '../edit-orderline-form/edit-orderline-form.component';
import { EditPriceFormComponent } from '../edit-price-form/edit-price-form.component';
import { SelectTicketCustomerComponent } from '../select-ticket-customer/select-ticket-customer.component';
import { AccountManagerService, ApplyDiscountCode, DeliveryMethod, OrderRenewalService, PendingOrderLine, PendingOrderLineViewWithPricing, PendingOrderService, PendingOrderView, DiscountService, PendingOrderLineService, UpdateTicketPriceComponentArgs, PutOrderlinePropertiesArgs, OrderManagementService, PlaceResult, PlacementError } from '../../api';
import { GenericErrorHandler } from '../../helpers/generic-error-handler';
import { ResultHandler } from '../../common/result-handler';

interface COChanges {
  amount: number;
  priceComponents: any;
  priceVariationId: number;
}

interface ConfirmOrderDialogData {
  title: string;
  isReadOnly: boolean;
  order: PendingOrderView;
  customer: any;
  isRenewal?: boolean;
  renewalPayByInvoice?: boolean;
  renewalReference?: string;
}

interface DeliveryMethodExtended extends DeliveryMethod {
  MapsTo?: DeliveryMethodExtended;
}

@Component({
    selector: 'app-confirm-order',
    templateUrl: './confirm-order.component.html',
    styleUrls: ['./confirm-order.component.scss'],
    standalone: false
})
export class ConfirmOrderComponent implements OnInit, AfterViewInit {

  constructor(@Inject(MAT_DIALOG_DATA) public passedData: ConfirmOrderDialogData,
    public dialogRef: MatDialogRef<ConfirmOrderComponent>,
    private dialog: MatDialog,
    private apploader: HttpLoaderService,
    private pendingOrderService: PendingOrderService,
    private orderManagementService: OrderManagementService,
    private orderRenewalService: OrderRenewalService,
    private confirmService: ConfirmService,
    private resultHandler: ResultHandler,
    private genericErrorHandler: GenericErrorHandler,
    private accountManagerService: AccountManagerService,
    private discountService: DiscountService,
    private translateService: TranslateService,
    private pendingOrderLineService: PendingOrderLineService
    ) {
  }

  public form: FormGroup;
  public orderDetails = null;
  public billingCustomer = null;
  public accountManagers: any = null;
  public saving = false;
  selectedObjects = [];
  pagedOrderLines: Array<any> = [];
  pagedDiscountLines: Array<any> = [];

  // availableSeasons = [];
  lastUsedPage = null;
  lastUsedPageForDiscounts = null;
  validDeliveryMethodSelected = false;
  deliveryMethods: Array<DeliveryMethodExtended> = null;
  // Part of a feature where the result was not what the client wanted. It's not used but the logic stays.
  private additionalDeliveryMethods = [
    //   {
    //   id: -2,
    //   mapsTo: 'E-TICKET',
    //   translationCode: 'DELIVERY-METHOD.DIRECT-DOWNLOAD',
    //   name: 'Direct Download (E-Ticket)'
    // }
  ];

  public invoiceBatchOptions: {Name: string, Value: string}[] = [{
    Name: this.translateService.instant('SALES.ORDERS.DETAILS.DROPDOWN.PROCESS-IN-INVOICE-BATCH'), Value: '1'
  }, {
    Name: this.translateService.instant('SALES.ORDERS.DETAILS.DROPDOWN.PROCESS-MANUALLY'), Value: '0'
  }];
  public defaultInvoiceBatchOption = this.invoiceBatchOptions[0];

  @ViewChild('pagerComponent') pagerComponent;
  @ViewChild('pagerComponentForDiscounts') pagerComponentForDiscounts;
  @ViewChildren('amountInput') amountInputs;

  public ProductTypeEnum = ProductTypeEnum;

  public hasMatchingPriceVariant(ol: any): boolean {
    if (ol?.PriceVariationId == null || !ol.PriceVariants) return false;
    return ol.PriceVariants.some((v: any) => v.VariantId === ol.PriceVariationId);
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.pagerComponent.pageSize = 5;
      this.pagerComponentForDiscounts.pageSize = 5;

      this.setSearchAndPage(1);
      this.setSearchAndPageForDiscounts(1);
    });
    this.pendingOrderService.getInvoiceBatchDefault()
      .subscribe(invoiceBatchDefault => {
        if (invoiceBatchDefault.length == 0) {
          return;
        }

        this.form.patchValue({
          IncludeInInvoiceBatch: invoiceBatchDefault
        });
      });
  }

  ngOnInit() {
    this.orderDetails = this.passedData.order;

    this.billingCustomer = this.passedData.customer;
    this.loadDependentFormData();

    const o = {
      OrderId: this.orderDetails.Id,
      AccountManagerId: this.billingCustomer.AccountManagerId,
      Reference: this.orderDetails.Reference,
      PayByInvoice: (this.billingCustomer.CustomerType === 1 ? '1' : '0'),
      DeliveryMethodId: this.orderDetails.DeliveryMethodId,
      IncludeInInvoiceBatch: '1'
    };

    this.form = new FormGroup({
      OrderId: new FormControl(''), // Passed to preserve value
      AccountManagerId: new FormControl({
        value: this.billingCustomer.AccountManagerId,
        disabled: this.billingCustomer.CustomerType !== 1
      }),
      Reference: new FormControl(''),
      PayByInvoice: new FormControl(''),
      DeliveryMethodId: new FormControl(''),
      IncludeInInvoiceBatch: new FormControl('')
    });

    this.form.patchValue(o);
    if (this.passedData.isRenewal) {
      this.form.patchValue({
        PayByInvoice: this.passedData.renewalPayByInvoice ? '1' : '0',
        Reference: this.passedData.renewalReference ?? '',
      });
    }
    if (this.orderDetails.DeliveryMethodId) {
      this.onDeliveryMethodChange({ Id: this.orderDetails.DeliveryMethodId });
    }
  }

  loadDependentFormData() {

    this.apploader.open();

    const call0 = this.accountManagerService.listActiveAccountManagers();
    const call1 = this.pendingOrderService.getDeliveryMethods(this.orderDetails.Id);

    forkJoin([call0, call1]).subscribe(([ams, deliveryMethods]) => {
      this.accountManagers = ams;
      const mappedAdditionalDeliveryMethods: Array<DeliveryMethodExtended> = this.additionalDeliveryMethods.map(adm => {
        const target = deliveryMethods.find(dm => dm.Code === adm.mapsTo);
        return {
          Id: adm.id,
          TranslationCode: adm.translationCode,
          Name: adm.name,
          MapsTo: target,
          Code: target.Code,
          DefaultPricingMethodId: target.DefaultPricingMethodId,
          IsPrintDefault: target.IsPrintDefault,
          AvailableUntilHoursBeforeEvent: target.AvailableUntilHoursBeforeEvent,
          AvailableFromHoursBeforeEvent: target.AvailableFromHoursBeforeEvent,
          DefaultEnabled: target.DefaultEnabled,
          Price: target.Price,
          IsSystemMethod: target.IsSystemMethod,
          CreateHardCard: target.CreateHardCard,
          PriceComponentId: target.PriceComponentId,
          PricingMethod: target.PricingMethod,
          PricingMethodName: target.PricingMethodName,
          PricingMethodTranslationCode: target.PricingMethodTranslationCode,
        };
      });
      this.deliveryMethods = deliveryMethods.concat(mappedAdditionalDeliveryMethods);
      this.apploader.close();
    });
  }

  getMappedToDeliveryMethod(dm: DeliveryMethodExtended): DeliveryMethod {
    if (dm.MapsTo == null) {
      return dm;
    }

    return this.getMappedToDeliveryMethod(dm.MapsTo);
  }

  onDeliveryMethodChange(val: DeliveryMethodExtended | { Id: number }) {
    this.validDeliveryMethodSelected = true;
    if (!val.hasOwnProperty('Code')) {
      this.setDeliveryMethod(val.Id);
      return;
    }

    const dm = val as DeliveryMethodExtended;
    const deliveryMethod = this.getMappedToDeliveryMethod(dm);
    this.setDeliveryMethod(deliveryMethod.Id);
  }

  setDeliveryMethod(newId: any) {
    this.apploader.open();
    this.pendingOrderService.putDeliveryMethod(this.orderDetails.Id, newId).subscribe((data: PendingOrderLine[]) => {
      this.apploader.close();
      this.refetchOrderLines();
      this.clearSelection();
    });
  }

  doPage(pageArgs) {
    this.setSearchAndPage(pageArgs.pageNr);
  }

  doPageForDiscounts(pageArgs) {
    this.setSearchAndPageForDiscounts(pageArgs.pageNr);
  }

  setSearchAndPage(pageNr: number) {
    this.lastUsedPage = pageNr;
    const length = this.orderDetails.OrderLines.filter(ol => ol.ProductType !== 5).length; // we need a copy
    this.pagerComponent.setObjectCount(length);
    this.pagedOrderLines = this.orderDetails.OrderLines
      .filter(ol => ol.ProductType !== 5)
      .slice((pageNr - 1) * this.pagerComponent.pageSize, (pageNr) * this.pagerComponent.pageSize);
    this.pagerComponent.setPage(pageNr);
  }

  setSearchAndPageForDiscounts(pageNr: number) {
    this.lastUsedPageForDiscounts = pageNr;
    const length = this.orderDetails.DiscountTotals.length; // we need a copy
    this.pagerComponentForDiscounts.setObjectCount(length);
    this.pagedDiscountLines = this.orderDetails.DiscountTotals
      .slice((pageNr - 1) * this.pagerComponentForDiscounts.pageSize, (pageNr) * this.pagerComponentForDiscounts.pageSize);
    this.pagerComponentForDiscounts.setPage(pageNr);
  }

  removeDiscountCode(codeId: number) {
    this.confirmService.confirm({
      title: this.translateService.instant('GENERIC.CONFIRM.WARNING.TITLE.ARE-YOU-SURE'),
      message: this.translateService.instant('SALES.ORDERS.DIALOG.MESSAGE.DISCOUNT-WILL-BE-REMOVED')
    }).subscribe((r: boolean) => {
      if (!r) {
        return;
      }

      this.apploader.open();
      this.discountService.removeDiscountCode(codeId, this.orderDetails.Id).subscribe((order: PendingOrderView) => {
        this.loadOrderLines(order);
        this.apploader.close();
      });
    });
  }

  addDiscountCode() {
    const title = this.translateService.instant('SALES.ORDERS.DIALOG.TITLE.PUT-IN-DISCOUNT');

    const options = DialogHelper.GetDialogOptions(DialogWidth.lg, { title: title });
    const dialogRef: MatDialogRef<any> = this.dialog.open(AddDiscountCodeFormComponent, options);

    dialogRef.afterClosed()
      .subscribe(res => {
        if (!res) {
          return;
        }

        this.apploader.open();
        this.discountService.applyDiscountCode(res.value, this.orderDetails.Id).subscribe((response: ApplyDiscountCode) => {
          this.apploader.close();
          if (response.Success) {
            this.loadOrderLines(response.UpdatedPendingOrder);
            return;
          }

          let error: string;

          switch (response.Reason) {
            case DiscountRejectionReason.CodeAlreadyUsed:
              error = 'DISCOUNT.REJECTION-REASON.CODE-ALREADY-USED';
              break;
            case DiscountRejectionReason.CodeNotFound:
              error = 'DISCOUNT.REJECTION-REASON.CODE-NOT-FOUND';
              break;
            case DiscountRejectionReason.DiscountExpired:
              error = 'DISCOUNT.REJECTION-REASON.DISCOUNT-EXPIRED';
              break;
            case DiscountRejectionReason.DiscountInactive:
              error = 'DISCOUNT.REJECTION-REASON.DISCOUNT-NOT-ACTIVE';
              break;
            case DiscountRejectionReason.DiscountNotFound:
              error = 'DISCOUNT.REJECTION-REASON.CODE-NOT-FOUND';
              break;
            case DiscountRejectionReason.DiscountNotStarted:
              error = 'DISCOUNT.REJECTION-REASON.DISCOUNT-NOT-STARTED';
              break;
            case DiscountRejectionReason.NotApplicable:
              error = 'DISCOUNT.REJECTION-REASON.DISCOUNT-NOT-APPLICABLE';
              break;
            case DiscountRejectionReason.NotStackable:
              error = 'DISCOUNT.REJECTION-REASON.DISCOUNT-NOT-STACKABLE';
              break;
            default:
              error = 'DISCOUNT.REJECTION-REASON.UNKNOWN';
              break;
          }

          this.confirmService.confirm({
            title: this.translateService.instant('DISCOUNT.REJECTION.TITLE'),
            message: this.translateService.instant(error),
            okonly: true
          }).subscribe();
        });
      });
  }

  changeTicketCustomer() {
    if (this.selectedObjects.length === 0) {
      return;
    }

    const title = this.translateService.instant('SALES.ORDERS.DIALOG.TITLE.CHANGE-TICKET-HOLDER');

    const options = DialogHelper.GetDialogOptions(DialogWidth.xxlg, { title: title, payload: this.selectedObjects });
    const dialogRef: MatDialogRef<any> = this.dialog.open(SelectTicketCustomerComponent, options);


    dialogRef.afterClosed()
      .subscribe(updated => {
          if (!updated) {
            return;
          }

          this.refetchOrderLines();
      });
  }

  editOrderLines() {
    if (this.selectedObjects.length === 0) {
      return;
    }

    const title = this.translateService.instant('SALES.ORDERS.DIALOG.TITLE.CHANGE-ORDER-LINES');
    const options = DialogHelper.GetDialogOptions(DialogWidth.md, { title: title, payload: this.selectedObjects, AllPriceVariants: this.orderDetails.AllPriceVariants });
    const dialogRef: MatDialogRef<any> = this.dialog.open(EditOrderLineFormComponent, options);


    dialogRef.afterClosed()
      .subscribe(updated => {
          if (!updated) {
            return;
          }

          this.refetchOrderLines();
          this.clearSelection();
        });
  }

  openOrderLinesPriceComponent() {
    if (this.selectedObjects.length === 0) {
      return;
    }

    this.pendingOrderLineService.getPriceComponents(this.selectedObjects[0].Id).subscribe((data: Array<any>) => {
      const title = this.translateService.instant('SALES.ORDERS.DIALOG.TITLE.EDIT-PRICE-STRUCTURE');
      const options = DialogHelper.GetDialogOptions(DialogWidth.md, {
        title: title,
        readOnly: false,
        priceLineComponentSelections: data,
        clearComponentPrice: true
      });
      const dialogRef: MatDialogRef<any> = this.dialog.open(EditPriceFormComponent, options);

      dialogRef.afterClosed()
        .subscribe(lines => {
          let args : UpdateTicketPriceComponentArgs;
          if (lines === false) {
            // cancel
          }
          else if (lines === null) {
            // clear prices
            const orderLineIds = this.selectedObjects.map(ol => ol.Id);
            data.forEach(line => {
              line.AmountExVAT = null;
              line.AmountInVAT = null;
              line.VAT = 0;
            });

            args = {
              OrderLineIds: orderLineIds,
              PriceComponents: data
            };

            this.pendingOrderLineService.updateTicketPriceComponents(args).subscribe((result: any) => {
              this.refetchOrderLines();
              this.clearSelection();
            });
          }
          else {
            // store data and update price
            const orderLineIds = this.selectedObjects.map(ol => ol.Id);

            args = {
              OrderLineIds: orderLineIds,
              PriceComponents: lines
            };

            this.pendingOrderLineService.updateTicketPriceComponents(args).subscribe((result: any) => {
              this.refetchOrderLines();
              this.clearSelection();
            });
          }
        });
    });
  }

  openOrderLinePriceComponent(ol) {
    this.pendingOrderLineService.getPriceComponents(ol.Id).subscribe(data => {
      const title = this.translateService.instant('SALES.ORDERS.DIALOG.TITLE.EDIT-PRICE-STRUCTURE');
      const options = DialogHelper.GetDialogOptions(DialogWidth.md, {
        title: title,
        readOnly: false,
        priceLineComponentSelections: data
      });
      const dialogRef: MatDialogRef<any> = this.dialog.open(EditPriceFormComponent, options);

      dialogRef.afterClosed()
        .subscribe(lines => {
          if (lines) {
            // store data and update price
            ol.Total = lines.reduce((prev, curr) => prev + curr.AmountInVAT, 0);
            this.updateLineProperties(ol, { priceComponents: lines } as COChanges);
          }
        }
        );
    });
  }

  refetchOrderLines() {

    this.apploader.open();

    // refetch the details.
    this.pendingOrderService.getPendingOrderDetails(this.orderDetails.Id).subscribe((order: PendingOrderView) => {
      this.loadOrderLines(order);
      this.apploader.close();
    });
  }

  loadOrderLines(order: any) {
    this.orderDetails = order;

    if (this.lastUsedPage != null) {
      this.setSearchAndPage(this.lastUsedPage);
      this.setSearchAndPageForDiscounts(this.lastUsedPageForDiscounts);
    } else {
      this.setSearchAndPage(1);
      this.setSearchAndPageForDiscounts(1);
    }
  }

  submit() {
    this.placePendingOrder();
  }

  private placePendingOrder() {
    this.saving = true;
    this.apploader.open();

    const formValue = this.form.value;
    const body = {
      PendingOrderId: this.orderDetails.Id,
      Reference: formValue.Reference,
      PayByInvoice: formValue.PayByInvoice === "1",
      IncludeInInvoiceBatch: formValue.IncludeInInvoiceBatch === "1",
      SendOrderConfirmationMail: false,
      AccountManagerId: formValue.AccountManagerId
    };

    const call$ = this.passedData.isRenewal
      ? this.orderRenewalService.placeOrderRenewal(this.orderDetails.Id, body)
      : this.orderManagementService.createNewOrder(body);

    call$.subscribe(
      result => {
        this.saving = false;
        this.apploader.close();

        const placementError: PlacementError | undefined = result?.Value?.PlacementError;
        if (placementError) {
          this.showPlacementError(placementError).subscribe(_ => {
            const denied: PlaceResult = { Handled: false, OrderDenied: false, UnavailableLineIds: [] };
            this.dialogRef.close(denied);
          });
          return;
        }

        const observable = this.resultHandler.handle(result);
        const returnValue: PlaceResult = { Handled: true, Id: result.Value?.Order?.Id };
        if (observable) {
          observable.subscribe(_ => this.dialogRef.close(returnValue));
        }
        else {
          this.dialogRef.close(returnValue);
        }
      },
      error => {
        this.saving = false;
        this.apploader.close();

        if (!this.resultHandler.handle(error?.error)) {
          this.genericErrorHandler.handle(error);
        }
      }
    );
  }

  private showPlacementError(placementError: PlacementError) {
    const args: any = {
      lineRef: placementError.OffendingOrderLineRef ?? '',
      row: placementError.OffendingPosition?.Row ?? '',
      column: placementError.OffendingPosition?.Column ?? '',
      seatNumber: placementError.OffendingPosition?.SeatNumber ?? '',
      rowNumber: placementError.OffendingPosition?.RowNumber ?? '',
      entranceId: placementError.OffendingEntranceId ?? '',
    };
    const message = this.translateService.instant(placementError.TranslationKey ?? '', args);
    return this.confirmService.confirm({
      title: this.translateService.instant('GENERIC.CONFIRM.TITLE.PROBLEMS'),
      message: message,
      okonly: true,
    });
  }

  toggleSelection(orderLine) {
    if (orderLine == null) { return; }

    if (this.isItemSelected(orderLine)) {
      const index = this.selectedObjects.indexOf(orderLine);
      this.selectedObjects.splice(index, 1);
    } else {
      this.selectedObjects.push(orderLine);
    }
  }

  isItemSelected(event) {

    if (!event) { return null; }

    if (this.selectedObjects == null || this.selectedObjects.length === 0) {
      return false;
    }

    const found = this.selectedObjects.filter(function (ep) {
      if (ep.Id === event.Id) {
        return ep;
      }
    });

    return (found != null && found.length > 0);
  }

  allSelectionToggle(toggleOn) {
    this.selectedObjects = [];
    if (toggleOn) {
      for (const ol of this.orderDetails.OrderLines) {
        this.selectedObjects.push(ol);
      }
    }
  }

  clearSelection() {
    this.selectedObjects = [];
  }

  onPriceVariationChange(e: MatSelectChange, ol) {
    const pv = ol.PriceVariants.find(_pv => _pv.VariantId === e.value);
    this.updateLineProperties(ol, { priceVariationId: e.value } as COChanges);
  }

  onInputFieldClick(e) {
    e.stopPropagation();
  }

  updateLineProperties(ol, changes: COChanges) {
    const ids = [ol.Id];
    const args : PutOrderlinePropertiesArgs = {
      SelectedOlIds: ids,
      TicketTextId: -1,
      EntranceId: -1,
      TicketPrice: changes.amount,
      PriceVariationId: changes.priceVariationId,
      PriceComponents: changes.priceComponents
    };

    this.apploader.open();

    this.pendingOrderLineService.putPendingOrderLineProperties(args).subscribe((updatedLine: PendingOrderLineViewWithPricing) => {

      // update the line
      let index = -1;

      index = this.orderDetails.OrderLines.map(function (e) {
        return e.Id;
      }).indexOf(updatedLine.Id);

      if (index >= 0) {
        this.orderDetails.OrderLines[index] = updatedLine;
      }

      this.setSearchAndPage(this.lastUsedPage);
      this.setSearchAndPageForDiscounts(this.lastUsedPageForDiscounts);

      // deselect the selection
      this.clearSelection();

      this.apploader.close();

    });
  }

  calculateTotal(property) {
    let result = 0;

    for (const ol of this.orderDetails.OrderLines) {
      result += ol[property];
    }

    return result;
  }
}
